import { message, Select } from 'antd';
import React, { useRef, useEffect, useState } from 'react';
import type { ActionType } from '@ant-design/pro-table';
import type { ProFormInstance } from '@ant-design/pro-form';
import { ModalForm, ProFormItem, ProFormText, ProFormTextArea } from '@ant-design/pro-form';
import { addGroup, updateGroup } from '@/services/api';
import type { API } from '@/services/typings';
import { GrossGropType_UserGroupToDeviceGroup, GrossGropType_UserToDeviceGroup, GroupTypeDevice, NamesPurpose_EditDeviceGroup, t } from '../../global';
import { NameEditor } from '../DeviceList/DevicesTable';

type NewOrUpdateFormProps = {
  isNew: boolean;
  visible: boolean;
  allGroups: string[];
  allUsers: string[];
  canEdit: boolean;
  onVisibleChange: React.Dispatch<React.SetStateAction<boolean>>;
  actionRef: React.MutableRefObject<ActionType | undefined>;
  initialValues: API.GroupListItem | undefined;
  handleModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  handleUpdateModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const NewOrUpdateForm: React.FC<NewOrUpdateFormProps> = (props) => {
  const { isNew, visible, allGroups, allUsers, canEdit, onVisibleChange, actionRef, initialValues, handleModalVisible, handleUpdateModalVisible } = props;
  const [options, setOptions] = useState<{ value: string }[]>([]);
  const [isRulesChanged, setIsRulesChanged] = useState<boolean>(false);
  const formRef = useRef<ProFormInstance<API.GroupListItem>>();

  useEffect(() => {
    if (!visible) return;
    if (isNew) return;
    if (initialValues == undefined) return;

    formRef.current?.setFieldValue('user_rules', initialValues?.accessed_from.filter((x) => x.type == GrossGropType_UserToDeviceGroup).map((x) => x.name));
    formRef.current?.setFieldValue('user_group_rules', initialValues?.accessed_from.filter((x) => x.type == GrossGropType_UserGroupToDeviceGroup).map((x) => x.name));
  }, [initialValues, isNew, visible])

  if (!visible) return null;

  const updateOptions = (isUser: boolean) => {
    const selectedOptions = formRef.current?.getFieldValue(isUser ? 'user_rules' : 'user_group_rules') || [];
    const isNotSelected = (g: string) => selectedOptions.find((v: string) => v === g) === undefined;
    if (isUser) {
      setOptions(allUsers.filter((u) => isNotSelected(u)).map((x) => ({ value: x })));
    } else {
      setOptions(allGroups.filter((g) => isNotSelected(g)).map((x) => ({ value: x })));
    }
  };

  const crossGroupSelect = (isUser: boolean, disabled: boolean = false) => {
    return (
      <Select
        showSearch
        allowClear={true}
        mode='multiple'
        disabled={disabled}
        placeholder={isUser ? t('Select a user') : t('Select a group')}
        onDropdownVisibleChange={(v) => {
          if (v) {
            updateOptions(isUser);
          }
        }}
        onChange={(v) => {
          v.sort();
          updateOptions(isUser);
          setIsRulesChanged(true);
        }}
      >
        {
          options.map((x) => (
            <Select.Option key={x.value} value={x.value}>
              {x.value}
            </Select.Option>
          ))
        }
      </Select>
    );
  }

  const processData = (data: any) => {
    data.type = GroupTypeDevice;
    if (isRulesChanged) {
      data.allowed_incomings = [];
      if (data.user_group_rules) {
        data.allowed_incomings = data.user_group_rules?.map((x: any) => ({ name: x, type: GrossGropType_UserGroupToDeviceGroup }));
      }
      if (data.user_rules) {
        data.allowed_incomings = [...data.user_rules?.map((x: any) => ({ name: x, type: GrossGropType_UserToDeviceGroup })), ...data.allowed_incomings]
      }
    } else {
      delete data.allowed_incomings;
    }
    delete data.allowed_outgoings;
    delete data.user_rules;
    delete data.user_group_rules;
    setIsRulesChanged(false);
  }

  const handleAdd = async (data: any) => {
    const hide = message.loading(t('Adding'));
    try {
      processData(data);
      await addGroup({ data });
      hide();
      message.success(t('Added successfully'));
      return true;
    } catch (error) {
      hide();
      message.error(
        typeof error == 'string' ? (error as string) : t('Adding failed, please try again!'),
      );
      return false;
    }
  };

  const handleUpdate = async (fields: any, old: API.GroupListItem) => {
    const data = { guid: old.guid } as any;
    let changed = false;
    if (canEdit) {
      if ((fields.name || '') != (old.name || '')) {
        data.name = fields.name;
        changed = true;
      }
      if ((fields.note || '') != (old.note || '')) {
        data.note = fields.note || '';
        changed = true;
      }
    }
    if ((fields.strategy_name || '') != (old.strategy_name || '')) {
      data.strategy_name = fields.strategy_name || '';
      changed = true;
    }
    if (!changed && (!canEdit || !isRulesChanged)) {
      return true;
    }
    if (canEdit) {
      data.user_rules = fields.user_rules;
      data.user_group_rules = fields.user_group_rules;
    }
    const hide = message.loading(t('Updating'));
    try {
      processData(data);
      await updateGroup({ data });
      hide();

      message.success(t('Update is successful'));
      return true;
    } catch (error) {
      hide();
      message.error(
        typeof error == 'string' ? (error as string) : t('Update failed, please try again!'),
      );
      return false;
    }
  };

  return (
    <ModalForm
      formRef={formRef}
      title={t(isNew ? 'Create' : 'Edit')}
      open={visible}
      width="400px"
      initialValues={initialValues}
      onOpenChange={onVisibleChange}
      onFinish={async (value) => {
        const success = await (isNew ? handleAdd : handleUpdate)(
          value as API.GroupListItem,
          initialValues as API.GroupListItem,
        );
        if (success) {
          if (actionRef.current) {
            actionRef.current.reload();
          }
          handleModalVisible(false);
          handleUpdateModalVisible(false);
        }
      }}
    >
      <ProFormText
        fieldProps={{ autoComplete: 'off' }}
        disabled={!canEdit}
        rules={canEdit ? [
          {
            required: true,
            whitespace: true,
          },
          {
            min: 4,
            max: 300,
          },
        ] : []}
        width="md"
        name="name"
        label={t('Name', true)}
      />
      <ProFormTextArea
        fieldProps={{ autoComplete: 'off' }}
        disabled={!canEdit}
        rules={canEdit ? [
          {
            max: 300,
          },
        ] : []}
        width="md"
        name="note"
        label={t('Note', true)}
      />
      <ProFormItem
        name={'user_group_rules'}
        initialValue={initialValues?.accessed_from.filter((x) => x.type == GrossGropType_UserGroupToDeviceGroup).map((x) => x.name) || []}
        label={<div>{t('Allow connections', true)}&nbsp;<span style={{ fontWeight: 650 }}>{t('from', true)}&nbsp;</span>{t('user groups', true)}</div>}
        style={{ maxWidth: '328px' }} // md=328px
      >
        {crossGroupSelect(false, !canEdit)}
      </ProFormItem>
      <ProFormItem
        name={'user_rules'}
        initialValue={initialValues?.accessed_from.filter((x) => x.type == GrossGropType_UserToDeviceGroup).map((x) => x.name) || []}
        label={<div>{t('Allow connections', true)}&nbsp;<span style={{ fontWeight: 650 }}>{t('from', true)}&nbsp;</span>{t('users', true)}</div>}
        style={{ maxWidth: '328px' }} // md=328px
      >
        {crossGroupSelect(true, !canEdit)}
      </ProFormItem>
      <NameEditor
        table="strategy"
        field="strategy_name"
        label="Strategy"
        purpose={NamesPurpose_EditDeviceGroup}
      />
    </ModalForm>
  );
}

export default NewOrUpdateForm;
